import { useState } from 'react';
import { ArrowRight, Check, Plus } from 'lucide-react';

export function validQuestions(questions) {
  if (!Array.isArray(questions) || questions.length > 4) return false;
  const unique = values => new Set(values).size === values.length;
  return unique(questions.map(q => q?.id)) && questions.every(q => q && typeof q.id === 'string' && q.id.length <= 40
    && typeof q.title === 'string' && q.title.trim() && q.title.length <= 100 && typeof q.multiple === 'boolean'
    && Array.isArray(q.options) && q.options.length >= 2 && q.options.length <= 6 && unique(q.options.map(o => o?.id))
    && q.options.every(o => o && typeof o.id === 'string' && o.id.length <= 40 && typeof o.label === 'string' && o.label.trim() && o.label.length <= 60
      && (!o.description || (typeof o.description === 'string' && o.description.length <= 120))));
}

export function cleanAnswers(questions = [], answers = {}) {
  return Object.fromEntries(questions.map(q => {
    const answer = answers?.[q.id];
    const selected = q.options.filter(option => Array.isArray(answer?.selected) && answer.selected.includes(option.id)).map(option => option.id).slice(0, q.multiple ? 6 : 1);
    const custom = typeof answer?.custom === 'string' ? answer.custom.slice(0, 200) : '';
    return [q.id, { selected: custom.trim() && !q.multiple ? [] : selected, custom }];
  }));
}

export function formatAnswers(questions, answers, skip = false) {
  const clean = cleanAnswers(questions, answers);
  const lines = questions.map(q => {
    const answer = clean[q.id];
    const selected = q.options.filter(option => answer.selected.includes(option.id));
    const choices = selected.map(option => option.label);
    if (answer.custom.trim()) choices.push(answer.custom.trim());
    const descriptions = selected.filter(option => option.description).map(option => `  ${option.label}：${option.description}`);
    return [`${q.title}：${choices.join('、') || '暂未确定'}`, ...descriptions].join('\n');
  });
  return `我的选择：\n${lines.join('\n')}\n\n${skip ? '请先根据已知信息给一版方案，未定项保留待完善。' : '请结合这些选择整理方案；未选项不代表已委托决定，建议与已知要求分开。'}`;
}

export default function CreationQuestions({ questions, answers, onChange, onSubmit, disabled, draft = '' }) {
  const [error, setError] = useState('');
  const clean = cleanAnswers(questions, answers);
  const answered = questions.filter(q => clean[q.id].selected.length || clean[q.id].custom.trim()).length;
  const update = (q, next) => { setError(''); onChange({ ...clean, [q.id]: next }); };
  const submit = skip => {
    const text = `${formatAnswers(questions, clean, skip)}${draft.trim() ? `\n\n补充：${draft.trim()}` : ''}`;
    if (text.length > 4000) { setError('选择和补充合计超过 4,000 字，请精简补充后继续。'); return; }
    setError(''); onSubmit(text, skip);
  };
  return <section className="creation-questions" aria-label="一起确定创作方向">
    <header className="creation-questions__header"><div><span className="creation-questions__eyebrow">一起定个方向</span><h3>选你喜欢的，我们接着往下想。</h3></div><span className="creation-questions__count" aria-live="polite">{answered}<span> / {questions.length}</span></span></header>
    <div className="creation-questions__body">
      {questions.map((q, index) => <fieldset key={q.id} disabled={disabled} className="creation-questions__group">
        <legend><span className="creation-questions__number">{String(index + 1).padStart(2, '0')}</span>{q.title}<span className="creation-questions__kind">{q.multiple ? '可多选' : '单选'}</span></legend>
        <div className="creation-questions__options">
          {q.options.map((option, optionIndex) => {
            const selected = clean[q.id].selected.includes(option.id);
            return <label key={option.id} className={`creation-questions__option${selected ? ' is-selected' : ''}`}>
              <input type={q.multiple ? 'checkbox' : 'radio'} name={`intake-${q.id}`} checked={selected} onChange={() => update(q, {
                selected: q.multiple ? selected ? clean[q.id].selected.filter(id => id !== option.id) : [...clean[q.id].selected, option.id] : [option.id],
                custom: q.multiple ? clean[q.id].custom : '',
              })} />
              <span className="creation-questions__letter" aria-hidden="true">{selected ? <Check size={14} /> : String.fromCharCode(65 + optionIndex)}</span>
              <span className="creation-questions__copy"><strong>{option.label}</strong>{option.description && <span>{option.description}</span>}</span>
            </label>;
          })}
        </div>
        <label className={`creation-questions__custom${clean[q.id].custom.trim() ? ' is-filled' : ''}`}><Plus size={15} aria-hidden="true" /><input aria-label={`${q.title} — 其他想法`} placeholder="其他想法，也可以写在这里…" value={clean[q.id].custom} maxLength={200}
          onChange={event => update(q, { selected: q.multiple ? clean[q.id].selected : [], custom: event.target.value })} /></label>
      </fieldset>)}
    </div>
    <footer className="creation-questions__footer"><div><span>不用全选，没想好的可以留着。</span>{error && <p role="alert">{error}</p>}</div><button type="button" className="creation-questions__skip" disabled={disabled} onClick={() => submit(true)}>暂时跳过</button><button type="button" className="creation-questions__continue" disabled={disabled || !answered} onClick={() => submit(false)}>确定选择<ArrowRight size={15} /></button></footer>
  </section>;
}
